<template>
  <Modal
    :model-value="alertStore.isVisible"
    :close-on-click-outside="!!alertStore.options.allowOutsideClick"
    :close-on-escape-key="!!alertStore.options.allowEscapeKey"
    :content-class="'min-w-xl max-w-3xl w-fit mx-4'"
    data-testid="alert-modal"
    @update:model-value="(value: boolean) => !value && closeModal()"
  >
    <!-- Icon -->
    <div v-if="alertStore.options.icon" class="flex justify-center pb-6 pt-6">
      <div :class="getIconClasses()">
        <component :is="getIconComponent()" class="h-10 w-10" />
      </div>
    </div>

    <!-- Title -->
    <div v-if="alertStore.options.titleText || alertStore.options.title" class="px-6 pb-2 pt-4">
      <h3 class="text-center text-base font-semibold text-gray-900">
        {{ alertStore.options.titleText || alertStore.options.title }}
      </h3>
    </div>

    <!-- Content -->
    <div class="px-6 pb-4">
      <!-- Text Content -->
      <p
        v-if="alertStore.options.text"
        class="mb-4 whitespace-pre-line text-center text-sm text-gray-600"
      >
        {{ alertStore.options.text }}
      </p>

      <!-- HTML Content -->
      <div
        v-if="alertStore.options.html"
        class="mb-4 text-center text-sm text-gray-600"
        v-html="alertStore.options.html"
      />

      <!-- Input Field -->
      <div v-if="alertStore.options.input" class="mb-4 flex">
        <!-- Checkbox Input -->
        <Checkbox
          v-if="alertStore.options.input === 'checkbox'"
          ref="inputRef"
          v-model="inputValue"
          :label="alertStore.options.inputLabel"
        />

        <!-- Textarea Input -->
        <Textarea
          v-else-if="alertStore.options.input === 'textarea'"
          ref="inputRef"
          v-model:input-text="inputValue"
          :placeholder="alertStore.options.inputPlaceholder"
          :rows="4"
          :is-error="!!inputError"
          :hint="inputError"
          class="w-full"
          :label="alertStore.options.inputLabel"
          v-bind="alertStore.options.inputAttributes || {}"
          @keyup.escape="() => alertStore.options.allowEscapeKey && closeModal()"
        />

        <!-- Select Input -->
        <Select
          v-else-if="alertStore.options.input === 'select'"
          ref="inputRef"
          v-model="inputValue"
          :placeholder="alertStore.options.inputPlaceholder || 'Bitte wählen...'"
          :options="getSelectOptions()"
          :is-error="!!inputError"
          :hint="inputError"
          class="w-full"
          :label="alertStore.options.inputLabel"
          v-bind="alertStore.options.inputAttributes || {}"
          @keyup.escape="() => alertStore.options.allowEscapeKey && closeModal()"
        />

        <!-- Text/Email/Password/Number Input -->
        <Input
          v-else
          ref="inputRef"
          v-model="inputValue"
          :type="alertStore.options.input"
          :placeholder="alertStore.options.inputPlaceholder"
          :is-error="!!inputError"
          :hint="inputError"
          class="w-full"
          :label="alertStore.options.inputLabel"
          v-bind="alertStore.options.inputAttributes || {}"
          @keyup.escape="() => alertStore.options.allowEscapeKey && closeModal()"
        />

        <!-- Copy Button -->
        <div
          v-if="alertStore.options.showInputCopyButton"
          class="mx-2 flex flex-col justify-center"
        >
          <CopyToClipboardButton :text="String(inputValue)" class="my-2" />
        </div>
      </div>
    </div>

    <!-- Buttons -->
    <div class="flex flex-wrap justify-center gap-4 px-6 pb-6">
      <CVButton
        v-for="button in alertStore.options.buttons"
        :key="button.propertyName"
        :ref="(el) => setButtonRef(el, button.propertyName)"
        :variant="button.style"
        size="sm"
        @click="closeModal(button.propertyName)"
      >
        {{ button.text }}
      </CVButton>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import Modal from '@/components/Modal.vue';
import { useAlertStore } from '@/stores/alert.store';
import Input from '@/components/Input.vue';
import Select from '@/components/Select.vue';
import Textarea from '@/components/Textarea.vue';

// Icons
import CheckCircleIcon from '@heroicons/vue/24/outline/CheckCircleIcon';
import ExclamationTriangleIcon from '@heroicons/vue/24/outline/ExclamationTriangleIcon';
import XCircleIcon from '@heroicons/vue/24/outline/XCircleIcon';
import InformationCircleIcon from '@heroicons/vue/24/outline/InformationCircleIcon';
import QuestionMarkCircleIcon from '@heroicons/vue/24/outline/QuestionMarkCircleIcon';
import CVButton from './buttons/CVButton.vue';
import Checkbox from './Checkbox.vue';
import CopyToClipboardButton from './CopyToClipboardButton.vue';

const alertStore = useAlertStore();

const inputValue = ref();
const inputError = ref('');
const inputRef = ref<HTMLInputElement>();
const buttonRefs = ref<Record<string, HTMLButtonElement>>({});

const getIconClasses = () => {
  const baseClasses = 'w-10 h-10 flex items-center justify-center';

  switch (alertStore.options.icon) {
    case 'success':
      return `${baseClasses} rounded-lg bg-green-100 text-green-600`;
    case 'warning':
      return `${baseClasses} rounded-lg bg-yellow-100 text-yellow-600`;
    case 'error':
      return `${baseClasses} rounded-lg bg-red-100 text-red-600`;
    case 'info':
      return `${baseClasses} rounded-lg bg-blue-100 text-blue-600`;
    case 'question':
      return `${baseClasses} rounded-lg bg-gray-100 text-gray-600`;
    default:
      return `${baseClasses} rounded-lg`;
  }
};

const getIconComponent = () => {
  switch (alertStore.options.icon) {
    case 'success':
      return CheckCircleIcon;
    case 'warning':
      return ExclamationTriangleIcon;
    case 'error':
      return XCircleIcon;
    case 'info':
      return InformationCircleIcon;
    case 'question':
      return QuestionMarkCircleIcon;
    default:
      return null;
  }
};

const getSelectOptions = () => {
  const inputOptions = alertStore.options.inputOptions;
  if (!inputOptions) return [];

  // Check if it's already an array of SelectOption objects
  if (Array.isArray(inputOptions)) {
    return inputOptions.map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }

  // Convert object format to array format
  // {"US":"Storniert durch uns","CUSTOMER":"Storniert durch Kunde"}
  // becomes [{"value": "US", "label": "Storniert durch uns"}, ...]
  return Object.entries(inputOptions).map(([value, label]) => ({
    value,
    label: label as string,
  }));
};

const setButtonRef = (el: any, propertyName: string) => {
  if (el) {
    // CVButton is a Vue component, we need to access the actual DOM element
    buttonRefs.value[propertyName] = el.$el || el;
  }
};

const closeModal = async (propertyName?: string) => {
  // Handle input validation - check if this is a "confirm" type button
  const needsValidation = alertStore.options.buttons?.some(
    (button) => button.propertyName === propertyName && button.needsValidation,
  );

  if (needsValidation && alertStore.options.inputValidator) {
    const error = alertStore.options.inputValidator(inputValue.value);
    if (error) {
      inputError.value = error;
      return;
    }
  }

  const result: Record<string, boolean | string | undefined> = {
    value: alertStore.options.input ? inputValue.value : undefined,
  };

  // Set the clicked button to true, all others to false
  if (alertStore.options.buttons) {
    alertStore.options.buttons.forEach((button) => {
      result[button.propertyName] = button.propertyName === propertyName;
    });
  }

  alertStore.resolveAlert(result);
};

// Watch for alert visibility changes to handle focus
watch(
  () => alertStore.isVisible,
  (isVisible) => {
    if (isVisible) {
      // Set initial input value if provided
      inputValue.value = alertStore.options.inputValue;
    } else {
      inputValue.value = '';
      inputError.value = '';
    }
  },
  { immediate: true },
);
</script>
